import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Badge,
  Container,
  useTheme,
  alpha,
  Fade,
  Paper,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Search as SearchIcon,
  Dashboard,
  Person,
  ShoppingCart,
  MyLocation,
} from "@mui/icons-material";
import { routes } from "@/consts";
import { useSearch } from "@/contexts/SearchContext";
import { useCart } from "@/contexts/CartContext";
// import useProducts from "@/hooks/use-products";
import { useLocale } from "@/middleware";
import { useTranslate } from "@/locales";
import { LANG } from "@/types";
import LanguageSelector from "@/components/language-selector";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import MobileBottomNav from "@/components/MobileBottomNav";
import useItems from "@/hooks/use-item";
import { debounce } from "lodash";

// Styled select for search type
const SearchSelect = styled(Select)(({ theme }) => ({
  "& .MuiSelect-select": {
    padding: theme.spacing(1, 1.5),
    minWidth: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  "& .MuiSelect-icon": {
    display: "none",
  },
}));
const SearchSection = () => {
  const {
    searchQuery,
    searchType,
    setSearchQuery,
    setSearchType,
    setSearchBrand,
    setSearchItem,
  } = useSearch();
  const [value, setValue] = useState(searchQuery || "");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const navigate = useNavigate();
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const theme = useTheme();
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const { suggestions } = useItems();
  const [searchParams] = useSearchParams();

  // Initialize value from URL parameters
  useEffect(() => {
    const brand = searchParams.get("brand");
    const item = searchParams.get("item");
    const query = searchParams.get("query");
    if (brand) {
      setValue(brand);
      setSearchBrand(brand);
    } else if (item) {
      setValue(item);
      setSearchItem(item);
    } else if (query) {
      setValue(query);
      setSearchQuery(query);
    }
  }, [searchParams, setSearchBrand, setSearchItem, setSearchQuery]);
  // Perform the search (used for both Enter and button click)
  const handleSearch = useCallback(
    async (query: string) => {
      if (!query || searchType !== t("supplier_sidebar.products")) {
        setShowResults(false);
        return;
      }
      setIsSearching(true);
      try {
        const results = await suggestions(query);
        setSearchResults(results.data || []);
        setShowResults(true);
        // setSearchQuery(query); // Update context with the query
      } catch (error) {
        console.error("Search error:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [setSearchQuery]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        handleSearch(query);
      }, 500),
    [handleSearch]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);


  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setValue(newValue);

      if (newValue && searchType === t("supplier_sidebar.products")) {
        debouncedSearch(newValue);
      } else {
        setShowResults(false);
        setSearchResults([]);
      }
    },
    [debouncedSearch, searchType, t]
  );

  // Handle Enter key or button click
  const handleSearchAction = useCallback(async () => {
    if (!value) return;

    // Cancel any pending debounced search
    debouncedSearch.cancel();

    // Perform immediate search
    await handleSearch(value);

    // Navigate to search results page (like Amazon)
    // navigate(`${routes.searchResults}?query=${encodeURIComponent(value)}&type=${encodeURIComponent(searchType)}`);
    setShowResults(false); // Hide dropdown after navigation
  }, [value, handleSearch, navigate, searchType]);

  // Handle Enter key press
  const handleSearchKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleSearchAction();
      }
    },
    [handleSearchAction]
  );

  // Handle result click
  const handleResultClick = (type: string, value: string) => {
    if (type === "brands") {
      setSearchBrand(value);
      navigate(`${routes.marketPlace}?brand=${encodeURIComponent(value)}`);
    } else {
      setSearchItem(value);
      navigate(`${routes.marketPlace}?item=${encodeURIComponent(value)}`);
    }
    setShowResults(false);
    setSearchQuery("");
    setValue("");
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <Box
      ref={searchContainerRef}
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        maxWidth: { xs: 1, md: 600, lg: 800 },
        mx: { md: 4 },
        bgcolor: "background.paper",
        borderRadius: 4,
        boxShadow: 0,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <FormControl size="small" sx={{ minWidth: 48, flexShrink: 0 }}>
        <SearchSelect
          value={searchType}
          onChange={(e) => setSearchType(e.target.value as string)}
          sx={{
            borderRadius:
              locale === LANG.AR ? "0 24px 24px 0" : "24px 0 0 24px",
            bgcolor: "background.paper",
            border: "none",
            minWidth: 48,
            boxShadow: "none",
            "& fieldset": { border: "none" },
          }}
        >
          <MenuItem value={t("supplier_sidebar.products")}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShoppingCart sx={{ fontSize: 20 }} />
            </Box>
          </MenuItem>
          <MenuItem value={t("general.suppliers")}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Person sx={{ fontSize: 20 }} />
            </Box>
          </MenuItem>
        </SearchSelect>
      </FormControl>
      <TextField
        placeholder={
          searchType === t("supplier_sidebar.products")
            ? `${t("actions.search")} ${t("supplier_sidebar.products")}`
            : `${t("actions.search")} ${t("general.suppliers")}`
        }
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleSearchKeyDown}
        size="small"
        fullWidth
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 0,
            border: "none",
            boxShadow: "none",
            "& fieldset": { border: "none" },
            "&:hover fieldset": { border: "none" },
            "&.Mui-focused fieldset": { border: "none" },
          },
          "& .MuiInputBase-input": {
            padding: theme.spacing(1, 1.5),
            fontSize: "0.875rem",
          },
        }}
      />
      <IconButton
        onClick={handleSearchAction}
        disabled={isSearching}
        sx={{
          borderRadius: locale === LANG.AR ? "8px 0 0 8px" : "0 8px 8px 0",
          bgcolor: "#18181b",
          color: "white",
          width: 40,
          height: 40,
          ml: locale === LANG.AR ? 0 : 0.5,
          mr: locale === LANG.AR ? 0 : 0,
          "&:hover": {
            bgcolor: theme.palette.primary.dark,
          },
          "&:disabled": {
            bgcolor: theme.palette.action.disabled,
          },
        }}
      >
        <SearchIcon sx={{ fontSize: 20 }} />
      </IconButton>
      {showResults && searchResults.length > 0 && (
        <Fade in={showResults}>
          <Paper
            elevation={8}
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              mt: 1,
              maxHeight: 400,
              overflow: "auto",
              zIndex: 1000,
              borderRadius: 2,
            }}
          >
            <List sx={{ py: 0 }}>
              {searchResults.map((value: any) => (
                <ListItem
                  key={value.value}
                  onClick={() => handleResultClick(value.type, value.value)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  {/* <ListItemAvatar>
                    <Avatar
                      src={product.image || "https://via.placeholder.com/48"}
                      alt={product.name}
                      variant="rounded"
                    />
                  </ListItemAvatar> */}
                  <ListItemText
                    primary={value.value}
                    secondary={`${value.label} `}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      fontSize: "0.875rem",
                    }}
                    secondaryTypographyProps={{
                      fontSize: "0.75rem",
                      color: theme.palette.text.secondary,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Fade>
      )}
    </Box>
  );
};
function StoreHeader() {
  // const { allMarketplaceProduct } = useItems();
  const { totalItems, inquiryItems } = useCart();
  // const [searchResults, setSearchResults] = useState([]);
  // const [showResults, setShowResults] = useState(false);
  // const [isSearching, setIsSearching] = useState(false);
  // const navigate = useNavigate();
  // const searchContainerRef = useRef<HTMLDivElement>(null);
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  // Memoized search click handler

  // Memoized key down handler

  // Memoized input change handler

  // Memoized result click handler

  // useEffect(() => {
  //   const handleClickOutside = (event: MouseEvent) => {
  //     if (
  //       searchContainerRef.current &&
  //       !searchContainerRef.current.contains(event.target as Node)
  //     ) {
  //       setShowResults(false);
  //     }
  //   };

  //   document.addEventListener("mousedown", handleClickOutside);
  //   return () => document.removeEventListener("mousedown", handleClickOutside);
  // }, []);

  // Cleanup debounced function on unmount
  // useEffect(() => {
  //   return () => {
  //     debouncedSearch.cancel();
  //   };
  // }, [debouncedSearch]);

  // Memoized search section to prevent re-renders
  // const SearchSection = () => {
  //   const { searchQuery, searchType, setSearchQuery, setSearchType } =
  //     useSearch();
  //   const [value, setValue] = useState(searchQuery || "");
  //   const [isSearching, setIsSearching] = useState(false);
  //   const [searchResults, setSearchResults] = useState([]);
  //   const [showResults, setShowResults] = useState(false);

  //   // Memoized debounced search function inside the component
  //   const debouncedSearch = useCallback(
  //     debounce(async (query: string) => {
  //       if (query && searchType === t("supplier_sidebar.products")) {
  //         setIsSearching(true);
  //         // setSearchQuery(query);

  //         try {
  //           const results = await allMarketplaceProduct({ search: query });
  //           setSearchResults(results || []);
  //           setShowResults(true);
  //         } catch (error) {
  //           console.error("Search error:", error);
  //           setSearchResults([]);
  //         } finally {
  //           setIsSearching(false);
  //         }
  //       } else {
  //         setShowResults(false);
  //       }
  //     }, 1000),
  //     []
  //   );

  //   // Cleanup debounced function on unmount
  //   // useEffect(() => {
  //   //   return () => {
  //   //     debouncedSearch.cancel();
  //   //   };
  //   // }, [debouncedSearch]);

  //   // Update local value when searchQuery changes
  //   // useEffect(() => {
  //   //   setValue(searchQuery || "");
  //   // }, [searchQuery]);

  //   const handleResultClick = useCallback(
  //     (productId: string) => {
  //       navigate(`${routes.productPage}/${productId}`);
  //       setShowResults(false);
  //       setSearchQuery("");
  //       setValue("");
  //     },
  //     [navigate, setSearchQuery]
  //   );

  //   const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //     const newValue = e.target.value;
  //     setValue(newValue);

  //     if (newValue && searchType === t("supplier_sidebar.products")) {
  //       debouncedSearch(newValue);
  //     } else {
  //       setShowResults(false);
  //     }
  //   };

  //   const handleSearchClick =async () => {
  //     if (value && searchType === t("supplier_sidebar.products")) {
  //       setIsSearching(true);
  //       try {
  //         const results = await allMarketplaceProduct({ search: value });
  //         setSearchResults(results || []);
  //         setShowResults(true);
  //       } catch (error) {
  //         console.error("Search error:", error);
  //         setSearchResults([]);
  //       } finally {
  //         setIsSearching(false);
  //       }
  //     }
  //   };

  //   const handleSearchKeyDown = useCallback(
  //     (event: React.KeyboardEvent) => {
  //       if (event.key === "Enter") {
  //         event.preventDefault();
  //         handleSearchClick();
  //       }
  //     },
  //     [handleSearchClick]
  //   );

  //   // Click outside handler
  //   useEffect(() => {
  //     const handleClickOutside = (event: MouseEvent) => {
  //       if (
  //         searchContainerRef.current &&
  //         !searchContainerRef.current.contains(event.target as Node)
  //       ) {
  //         setShowResults(false);
  //       }
  //     };

  //     document.addEventListener("mousedown", handleClickOutside);
  //     return () =>
  //       document.removeEventListener("mousedown", handleClickOutside);
  //   }, []);

  //   return (
  //     <Box
  //       ref={searchContainerRef}
  //       sx={{
  //         display: "flex",
  //         alignItems: "center",
  //         width: "100%",
  //         maxWidth: { xs: 1, md: 600, lg: 800 },
  //         mx: { md: 4 },
  //         bgcolor: "background.paper",
  //         borderRadius: 4,
  //         boxShadow: 0,
  //         border: `1px solid ${theme.palette.divider}`,
  //       }}
  //     >
  //       <FormControl size="small" sx={{ minWidth: 48, flexShrink: 0 }}>
  //         <SearchSelect
  //           value={searchType}
  //           onChange={(e) => setSearchType(e.target.value as string)}
  //           sx={{
  //             borderRadius:
  //               locale === LANG.AR ? "0 24px 24px 0" : "24px 0 0 24px",
  //             bgcolor: "background.paper",
  //             border: "none",
  //             minWidth: 48,
  //             boxShadow: "none",
  //             "& fieldset": { border: "none" },
  //           }}
  //         >
  //           <MenuItem value={t("supplier_sidebar.products")}>
  //             <Box
  //               sx={{
  //                 display: "flex",
  //                 alignItems: "center",
  //                 justifyContent: "center",
  //               }}
  //             >
  //               <ShoppingCart sx={{ fontSize: 20 }} />
  //             </Box>
  //           </MenuItem>
  //           <MenuItem value={t("general.suppliers")}>
  //             <Box
  //               sx={{
  //                 display: "flex",
  //                 alignItems: "center",
  //                 justifyContent: "center",
  //               }}
  //             >
  //               <Person sx={{ fontSize: 20 }} />
  //             </Box>
  //           </MenuItem>
  //         </SearchSelect>
  //       </FormControl>
  //       <TextField
  //         placeholder={
  //           searchType === t("supplier_sidebar.products")
  //             ? `${t("actions.search")} ${t("supplier_sidebar.products")}`
  //             : `${t("actions.search")} ${t("general.suppliers")}`
  //         }
  //         value={value}
  //         onChange={handleInputChange}
  //         onKeyDown={handleSearchKeyDown}
  //         size="small"
  //         fullWidth
  //         sx={{
  //           "& .MuiOutlinedInput-root": {
  //             borderRadius: 0,
  //             border: "none",
  //             boxShadow: "none",
  //             "& fieldset": { border: "none" },
  //             "&:hover fieldset": { border: "none" },
  //             "&.Mui-focused fieldset": { border: "none" },
  //           },
  //           "& .MuiInputBase-input": {
  //             padding: theme.spacing(1, 1.5),
  //             fontSize: "0.875rem",
  //           },
  //         }}
  //       />
  //       <IconButton
  //         onClick={handleSearchClick}
  //         disabled={isSearching}
  //         sx={{
  //           borderRadius: locale === LANG.AR ? "8px 0 0 8px" : "0 8px 8px 0",
  //           bgcolor: "#18181b",
  //           color: "white",
  //           width: 40,
  //           height: 40,
  //           ml: locale === LANG.AR ? 0 : 0.5,
  //           mr: locale === LANG.AR ? 0 : 0,
  //           "&:hover": {
  //             bgcolor: theme.palette.primary.dark,
  //           },
  //           "&:disabled": {
  //             bgcolor: theme.palette.action.disabled,
  //           },
  //         }}
  //       >
  //         <SearchIcon sx={{ fontSize: 20 }} />
  //       </IconButton>
  //       {showResults && searchResults.length > 0 && (
  //         <Fade in={showResults}>
  //           <Paper
  //             elevation={8}
  //             sx={{
  //               position: "absolute",
  //               top: "100%",
  //               left: 0,
  //               right: 0,
  //               mt: 1,
  //               maxHeight: 400,
  //               overflow: "auto",
  //               zIndex: 1000,
  //               borderRadius: 2,
  //             }}
  //           >
  //             <List sx={{ py: 0 }}>
  //               {searchResults.map((product: any) => (
  //                 <ListItem
  //                   key={product.id}
  //                   onClick={() => handleResultClick(product.id)}
  //                   sx={{
  //                     cursor: "pointer",
  //                     "&:hover": {
  //                       bgcolor: alpha(theme.palette.primary.main, 0.05),
  //                     },
  //                   }}
  //                 >
  //                   <ListItemAvatar>
  //                     <Avatar
  //                       src={product.image || "https://via.placeholder.com/48"}
  //                       alt={product.name}
  //                       variant="rounded"
  //                     />
  //                   </ListItemAvatar>
  //                   <ListItemText
  //                     primary={product.name}
  //                     secondary={`${product.price} ${t("currency")}`}
  //                     primaryTypographyProps={{
  //                       fontWeight: 600,
  //                       fontSize: "0.875rem",
  //                     }}
  //                     secondaryTypographyProps={{
  //                       fontSize: "0.75rem",
  //                       color: theme.palette.text.secondary,
  //                     }}
  //                   />
  //                 </ListItem>
  //               ))}
  //             </List>
  //           </Paper>
  //         </Fade>
  //       )}
  //     </Box>
  //   );
  // };

  const NavigationItem = ({
    icon,
    label,
    to,
    badge,
    onClick,
  }: {
    icon: React.ReactNode;
    label: string;
    to?: string;
    badge?: number;
    onClick?: () => void;
  }) => (
    <Box
      component={to ? Link : "div"}
      to={to}
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.5,
        px: 2,
        py: 1,
        borderRadius: 2,
        cursor: "pointer",
        textDecoration: "none",
        color: theme.palette.text.primary,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          color: theme.palette.primary.main,
        },
      }}
    >
      <Badge badgeContent={badge} color="error">
        <Box sx={{ color: "inherit" }}>{icon}</Box>
      </Badge>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          fontSize: "0.75rem",
          textAlign: "center",
          color: "inherit",
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  return (
    <>
      {/* Desktop Header */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: "background.paper",
          borderBottom: `1px solid ${theme.palette.divider}`,
          zIndex: 1200,
          display: { xs: "none", md: "block" },
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ py: 1 }}>
            <Fade in={true} timeout={600}>
              <Typography
                component={Link}
                to={routes.marketPlace}
                variant="h5"
                sx={{
                  fontWeight: 800,
                  textDecoration: "none",
                  color: "inherit",
                  background: "#18181b",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mr: 3,
                }}
              >
                Medex
              </Typography>
            </Fade>

            <SearchSection />

            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}
            >
              {authUser && (
                <NavigationItem
                  icon={
                    <Dashboard
                      sx={{ fontSize: 28, color: theme.palette.text.primary }}
                    />
                  }
                  label={t("navigation.Dashboard")}
                  to={routes.home}
                />
              )}

              <NavigationItem
                icon={
                  <MyLocation
                    sx={{ fontSize: 28, color: theme.palette.text.primary }}
                  />
                }
                label={t("actions.addTenderRequest")}
                to={routes.inquiryForm}
                badge={
                  inquiryItems.length > 0 ? inquiryItems.length : undefined
                }
              />

              {!authUser ? (
                <NavigationItem
                  icon={
                    <Person
                      sx={{ fontSize: 28, color: theme.palette.text.primary }}
                    />
                  }
                  label={t("navigation.logReg")}
                  to={routes.login}
                />
              ) : (
                <NavigationItem
                  icon={
                    <Person
                      sx={{ fontSize: 28, color: theme.palette.text.primary }}
                    />
                  }
                  label={authUser.nickName}
                  to={routes.settings}
                />
              )}

              <NavigationItem
                icon={
                  <ShoppingCart
                    sx={{ fontSize: 28, color: theme.palette.text.primary }}
                  />
                }
                label={t("buyer_pages.cart.title")}
                to={routes.cart}
                badge={totalItems > 0 ? totalItems : undefined}
              />

              <LanguageSelector />
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Search Bar */}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            bgcolor: "background.paper",
            borderBottom: `1px solid ${theme.palette.divider}`,
            p: 2,
            pt: 3,
          }}
        >
          <SearchSection />
        </Box>
      )}

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <MobileBottomNav
          items={[
            {
              icon: (
                <Person
                  sx={{ fontSize: 24, color: theme.palette.text.primary }}
                />
              ),
              label: t("profile.account"),
              to: routes.login,
            },
            {
              icon: (
                <MyLocation
                  sx={{ fontSize: 24, color: theme.palette.text.primary }}
                />
              ),
              label: "RFQ",
              to: routes.inquiryForm,
              badge: inquiryItems.length > 0 ? inquiryItems.length : undefined,
            },
            {
              icon: (
                <ShoppingCart
                  sx={{ fontSize: 24, color: theme.palette.text.primary }}
                />
              ),
              label: t("buyer_pages.cart.title"),
              to: routes.cart,
              badge: totalItems > 0 ? totalItems : undefined,
            },
            {
              icon: <LanguageSelector className="!flex !flex-col" />,
              label: "",
              to: "#",
            },
          ]}
        />
      )}

      {/* Spacer for mobile */}
      {isMobile && <Box sx={{ height: 100 }} />}
    </>
  );
}

export default StoreHeader;
